import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TriangleIcon, CheckIcon } from 'lucide-react';
import { CRESTMONT, CrestmontHeader } from '../components/crestmont/CrestmontChrome';
import { Footer } from '../components/Footer';
import { CrestmontLeadForm } from '../components/crestmont/CrestmontLeadForm';
import { CurriculumSection, SpecializationsSection } from '../components/crestmont/CrestmontCurriculum';
import { BenefitsSection, CtaBand, FaqSection } from '../components/crestmont/CrestmontDetails';
import { crestmontPrograms, getCrestmontProgram } from '../data/crestmontPrograms';
import { useMetaTags } from '../hooks/useMetaTags';

// Degree-specific Crestmont University landing page, built to the Crestmont
// brand guide. Routed at /universities/crestmont-university/<program-slug>;
// `programSlug` is also read from the path so an explicitly registered route
// (which carries no param) still resolves.
interface CrestmontProgramPageProps {
  /** Explicit slug when the route is registered without a param. */
  slug?: string;
}
export function CrestmontProgramPage({
  slug
}: CrestmontProgramPageProps) {
  const navigate = useNavigate();
  const params = useParams<{
    programSlug?: string;
  }>();
  // The site footer navigates by page key, matching the rest of the site.
  const handleNavigate = (page: string) => {
    navigate(page === 'compare' ? '/' : `/${page}`);
    window.scrollTo(0, 0);
  };
  const resolvedSlug = slug || params.programSlug || window.location.pathname.split('/').filter(Boolean).pop();
  const program = getCrestmontProgram(resolvedSlug);
  useMetaTags({
    title: program ? `${program.name} Online | Crestmont University` : 'Crestmont University',
    description: program?.heroSubhead || 'Online degree programs from Crestmont University.',
    robots: 'noindex, follow'
  });
  const scrollToForm = () => {
    const target = document.getElementById('apply');
    if (target) target.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  if (!program) {
    return <div className="min-h-screen bg-white flex flex-col" data-id="element-1459" data-mp="26b47g5mejdw9">
        <CrestmontHeader onApply={scrollToForm} data-id="element-1460" data-mp="21foudfw7xm0w" />
        <main className="flex-1 max-w-3xl mx-auto px-4 py-24 text-center" data-id="element-1461" data-mp="2f5sg85r6rdpt">
          <h1 className="text-2xl font-extrabold" style={{
          color: CRESTMONT.navy
        }} data-id="element-1462" data-mp="2f7wsbaz8nj16">
            This program isn’t available
          </h1>
          <p className="mt-3 text-sm text-gray-600" data-id="element-1463" data-mp="2p2x367suvd7o">
            Browse the full list of Crestmont degrees to find the right fit.
          </p>
          <a href="/universities/crestmont-university" className="mt-6 inline-block px-6 py-3 rounded text-sm font-bold" style={{
          backgroundColor: CRESTMONT.gold,
          color: CRESTMONT.ink
        }} data-id="element-1464" data-mp="2k79ctp2n0e0l">
            View all programs
          </a>
        </main>
        <Footer onNavigate={handleNavigate} data-id="element-1465" data-mp="13r5c4516w5ha" />
      </div>;
  }
  const programOptions = Object.values(crestmontPrograms).map(entry => ({
    label: entry.name,
    value: entry.mProgramValue
  }));
  return <div className="min-h-screen bg-white w-full" data-id="element-1466" data-mp="plmx0utsmcbi">
      <CrestmontHeader onApply={scrollToForm} data-id="element-1467" data-mp="tmgizinodh1r" />

      <main data-id="element-1468" data-mp="tm6qnof5qxsw">
        {/* Hero — the request form lives here so it is reachable without a
            scroll on phones. On mobile a compressed headline sits above the
            form; the full pitch follows underneath it. */}
        <section className="py-8 md:py-16 lg:py-20" style={{
        backgroundColor: CRESTMONT.navy
      }} data-id="element-1469" data-mp="ks1xgycjxaa7">
          <div className="max-w-6xl mx-auto px-4 grid gap-8 lg:grid-cols-5 lg:gap-12 lg:items-start" data-id="element-1470" data-mp="avyvptlm3dns">
            <div className="lg:col-span-3" data-id="element-1471" data-mp="3t23i40j6t8ed">
              
              <p className="lg:hidden text-[11px] font-bold uppercase tracking-[0.16em] text-[#fcc23f]" data-id="element-1472" data-mp="3t3o9jha7gb80">
                {program.degreeLevel} · 100% Online
              </p>
              <h1 className="mt-1.5 lg:mt-0 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-[1.1] lg:leading-[1.05] tracking-tight max-w-2xl" data-id="element-1473" data-mp="1rbs14gjc3hhg">
                {program.name}
                <span className="block text-white/60" data-id="element-1474" data-mp="3sbdc0d2p7ny8">({program.code})</span>
              </h1>
              <p className="mt-3 lg:mt-5 text-sm md:text-base text-white/75 max-w-xl leading-relaxed" data-id="element-1475" data-mp="3newq7piz1lob">
                {program.heroSubhead}
              </p>
              {/* Desktop-only supporting proof — on mobile the form takes this
                  space and the same content appears in the section below. */}
              <ul className="hidden lg:block mt-8 space-y-2.5 max-w-lg" data-id="element-1476" data-mp="39f55z71v391v">
                {program.positioningBullets.map(bullet => <li key={bullet} className="flex items-start gap-2.5" data-id="element-1477" data-mp="34lapsdlgtc7j">
                    <CheckIcon className="mt-0.5 w-4 h-4 flex-shrink-0" style={{
                  color: CRESTMONT.gold
                }} aria-hidden="true" data-id="element-1478" data-mp="2zok8xo1u6cl2" />
                    <span className="text-sm leading-relaxed text-white/80" data-id="element-1479" data-mp="2qnuh0vfp56cm">
                      {bullet}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="lg:col-span-2" data-id="element-1480" data-mp="116ql5cmqntbt">
              <CrestmontLeadForm programValue={program.mProgramValue} programName={program.name} programOptions={programOptions} data-id="element-1481" data-mp="115p6yzafeqpv" />
            </div>
          </div>
        </section>

        {/* Benefits + accreditation — sits directly under the hero so the
            credibility signals land before the longer program detail. */}
        <BenefitsSection benefits={program.benefits} data-id="element-1482" data-mp="1f3m5404j356n" />

        {/* Positioning */}
        <section className="py-12 md:py-20 bg-white" data-id="element-1483" data-mp="idmpjlpyn6ek">
          <div className="max-w-6xl mx-auto px-4 grid gap-8 lg:grid-cols-5 lg:gap-12 lg:items-start" data-id="element-1484" data-mp="mickn8csogyf">
            <div className="lg:col-span-3" data-id="element-1485" data-mp="rfcpjfxwrdny">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#00426b]/60" data-id="element-1486" data-mp="rekuc6883hy7">
                {program.positioningEyebrow}
              </p>
              <h2 className="mt-2 text-2xl md:text-4xl font-extrabold tracking-tight leading-tight max-w-lg" style={{
              color: CRESTMONT.navy
            }} data-id="element-1487" data-mp="3uwcs6191iw4b">
                {program.positioningHeading}
              </h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-gray-600 max-w-lg" data-id="element-1488" data-mp="16zmg1t8pivfq">
                {program.positioningBody}
              </p>
              {/* Mobile repeat of the hero proof points */}
              <ul className="lg:hidden mt-6 space-y-2.5" data-id="element-1489" data-mp="1bsfkeg09k1ra">
                {program.positioningBullets.map(bullet => <li key={bullet} className="flex items-start gap-2.5" data-id="element-1490" data-mp="o2v464vhvilp">
                    <CheckIcon className="mt-0.5 w-4 h-4 flex-shrink-0" style={{
                  color: CRESTMONT.navy
                }} aria-hidden="true" data-id="element-1491" data-mp="o4z308ejdrf4" />
                    <span className="text-sm leading-relaxed text-gray-700" data-id="element-1492" data-mp="x4m56wf3b198">
                      {bullet}
                    </span>
                  </li>)}
              </ul>
            </div>

            <dl className="lg:col-span-2 grid grid-cols-3 lg:grid-cols-1 gap-3" data-id="element-1493" data-mp="28hx07nsyn46p">
              {program.stats.map(stat => <div key={stat.label} className="rounded-md px-4 py-4 text-center lg:text-left" style={{
              backgroundColor: CRESTMONT.mist
            }} data-id="element-1494" data-mp="23ilj2doolwol">
                  <dt className="sr-only" data-id="element-1495" data-mp="2g1zaw2hnp06i">{stat.label}</dt>
                  <dd data-id="element-1496" data-mp="2g3k49os8id2s">
                    <span className="block text-lg sm:text-xl md:text-2xl font-extrabold tabular-nums" style={{
                  color: CRESTMONT.navy
                }} data-id="element-1497" data-mp="222g175975gs3">
                      {stat.value}
                    </span>
                    <span className="mt-1 block text-[10px] md:text-xs leading-snug text-gray-500" data-id="element-1498" data-mp="2uqvenncojr1b">
                      {stat.label}
                    </span>
                  </dd>
                </div>)}
            </dl>
          </div>
        </section>

        {/* Testimonial */}
        

        <CurriculumSection program={program} data-id="element-1499" data-mp="2pzvkgpuy3n2i" />
        <SpecializationsSection program={program} data-id="element-1500" data-mp="2pykh2r5ewhbx" />
        <CtaBand heading={program.ctaBand.heading} body={program.ctaBand.body} onApply={scrollToForm} data-id="element-1501" data-mp="3dpptewc58rhh" />
        <FaqSection faq={program.faq} data-id="element-1502" data-mp="3igzfxo2ebep5" />
      </main>

      <Footer onNavigate={handleNavigate} data-id="element-1503" data-mp="3iism7293jwwm" />
    </div>;
}